import React from 'react';
import { Loader2 } from 'lucide-react';
import { Id } from '@hero/schema';
import { cn } from '@hero/web-lib';
import type { SelectAsyncOptionRowColorClasses } from './SelectAsyncOptionRow';

const MAX_VISIBLE_MULTI = 3;

export interface SelectAsyncTriggerContentProps<T extends { _id: Id }> {
  selected: T | T[] | null;
  loading: boolean;
  placeholder: string;
  loadingMessage: string;
  mode: 'single' | 'multi';
  getItemId: (item: T) => Id;
  getDisplayFromItem: (item: T) => { label: string; desc?: string; subDesc?: string };
  useBuiltInTrigger: boolean;
  showAvatar: boolean;
  colorClasses: SelectAsyncOptionRowColorClasses | null;
  renderTrigger?: (selected: T | T[] | null) => React.ReactNode;
  /** @deprecated No longer needed — RTL is handled via Tailwind rtl: variants */
  isRTL?: boolean;
}

export function SelectAsyncTriggerContent<T extends { _id: Id }>({
  selected,
  loading,
  placeholder,
  loadingMessage,
  mode,
  getItemId,
  getDisplayFromItem,
  useBuiltInTrigger,
  showAvatar,
  colorClasses,
  renderTrigger,
}: SelectAsyncTriggerContentProps<T>) {
  if (loading) {
    return (
      <span className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
        <span>{loadingMessage}</span>
      </span>
    );
  }
  if (!selected || (Array.isArray(selected) ? selected.length === 0 : false)) {
    return <span className="text-muted-foreground">{placeholder}</span>;
  }
  if (renderTrigger) {
    return <>{renderTrigger(selected)}</>;
  }

  const arr = Array.isArray(selected) ? selected : [selected];
  const first = arr[0];
  const display = getDisplayFromItem(first);
  const label = display.label;

  if (arr.length === 1) {
    if (useBuiltInTrigger && showAvatar && colorClasses) {
      const initial = label.charAt(0).toUpperCase();
      return (
        <div className="flex items-center gap-2 min-w-0 flex-1 rtl:flex-row-reverse">
          <div
            className={cn(
              'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-xs',
              colorClasses.avatarBg,
              colorClasses.avatarText,
            )}>
            {initial}
          </div>
          <span className="truncate">{label}</span>
        </div>
      );
    }
    return <span className="truncate min-w-0 flex-1">{label}</span>;
  }

  const visible = arr.slice(0, MAX_VISIBLE_MULTI);
  const restCount = arr.length - MAX_VISIBLE_MULTI;

  return (
    <div className="flex min-w-0 flex-1 overflow-hidden rtl:flex-row-reverse rtl:justify-end">
      <div className="flex flex-wrap items-center gap-2 min-w-0 overflow-hidden rtl:flex-row-reverse">
        {visible.map((item: T) => {
          const id = getItemId(item);
          const itemDisplay = getDisplayFromItem(item);
          const itemLabel = itemDisplay.label;
          if (useBuiltInTrigger && showAvatar && colorClasses) {
            const initial = itemLabel.charAt(0).toUpperCase();
            return (
              <div
                key={id.toString()}
                className="flex items-center gap-1.5 flex-shrink-0 min-w-0 max-w-full rtl:flex-row-reverse">
                <div
                  className={cn(
                    'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-xs',
                    colorClasses.avatarBg,
                    colorClasses.avatarText,
                  )}>
                  {initial}
                </div>
                <span className="truncate text-sm max-w-[120px] min-w-0" title={itemLabel}>
                  {itemLabel}
                </span>
              </div>
            );
          }
          return (
            <span
              key={id.toString()}
              className="truncate text-sm text-gray-900 flex-shrink-0 max-w-[140px] min-w-0"
              title={itemLabel}>
              {itemLabel}
            </span>
          );
        })}
        {restCount > 0 && (
          <span
            className="flex-shrink-0 text-sm text-gray-500 font-medium"
            aria-label={`${restCount} more selected`}
            title={`${restCount} more selected`}>
            +{restCount} more
          </span>
        )}
      </div>
    </div>
  );
}
